/**
 * Pacific-time helpers shared by the client and Cloud Functions.
 *
 * The game day runs midnight-to-midnight in America/Los_Angeles wherever the
 * player is, so "which day is it" must be computed in that zone rather than the
 * viewer's.
 */

export const GAME_TIME_ZONE = 'America/Los_Angeles';

/*
 * Built from parts rather than from a formatted string.
 *
 * `toLocaleDateString('en-CA')` renders YYYY-MM-DD natively and was the obvious
 * shorthand, but the string it hands back is a *formatted* one: some ICU builds
 * wrap the fields in bidi control characters (U+200E and friends), which are
 * invisible everywhere except a string comparison. This value becomes half of a
 * Firestore document id, so an invisible character means the client looks up a
 * document the server never wrote, on that browser only, forever.
 *
 * Reading `year`, `month` and `day` off `formatToParts` skips every literal in
 * the pattern, so nothing but digits can survive. `en-US` rather than the
 * ambient locale because a locale with non-Latin digits would otherwise produce
 * a day string the server cannot match either.
 */
const pacificDate = new Intl.DateTimeFormat('en-US', {
  timeZone: GAME_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  numberingSystem: 'latn',
});

/**
 * The Pacific calendar day for an instant, as `YYYY-MM-DD`.
 *
 * Never constructs a Date from a formatted string, so it is correct in every
 * viewer timezone.
 *
 * Stored on each pun as `pstDay`, and computed server-side at submit time —
 * a client cannot claim a different day to get a second submission.
 */
export function pacificDayString(instant: Date = new Date()): string {
  const parts = pacificDate.formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${value('year')}-${value('month')}-${value('day')}`;
}

/**
 * Move a `YYYY-MM-DD` game day by a whole number of days.
 *
 * Calendar arithmetic on the string rather than clock arithmetic on an instant.
 * Subtracting 24 hours from a Date is wrong twice a year: on the days Pacific
 * shifts, "yesterday" is 23 or 25 hours back, and the naive version lands on
 * the wrong date or the same one twice.
 *
 * UTC is used purely as a calendar here. No time zone is involved, because a
 * game day is already just a label by the time it reaches this function.
 */
export function shiftPacificDay(day: string, deltaDays: number): string {
  const [year, month, date] = day.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, date));
  shifted.setUTCDate(shifted.getUTCDate() + deltaDays);

  return shifted.toISOString().slice(0, 10);
}
